// ETFE Retrofit Gallery — full-screen immersive overlay for the film-upgrade build-out.
// Opened from the growlab bottom bar or via /growlab?retrofit=1.
import React from "react";

const BASE = "/assets/growlab/retrofit";

const ITEMS = [
  {
    id: "retrofit-cutout", title: "The Fabric Cut",
    use: "The opening move: mark each wall and cut a large rectangular section out of the fabric with the WORKPRO blade — the folded-back flap becomes the template for your film panel.",
  },
  {
    id: "retrofit-frame", title: "2020 Aluminum T-Slot Track",
    use: "The skeleton: VEVOR 2020 extrusions joined by L-shape interior corner keys and 316 stainless 1/4\"-20 tension bolts — a rigid reusable frame that carries the film.",
  },
  {
    id: "retrofit-film", title: "ETFE Fluoropolymer Film",
    use: "The skin: CS Hyde or AGC F-CLEAN ETFE — crystal-clear and UV-transparent, so UVA and UVB pass straight through to the canopy (poly film and glass block UVB). Self-cleaning, and it shrugs off 90% humidity forever.",
  },
  {
    id: "retrofit-tension", title: "Wire-Lock Tensioning",
    use: "The tension system: PVC-coated spring wire pressed into lock channel drums the film tight — the Bootstrap Farmer bundle doubles as the film tensioning tool.",
  },
  {
    id: "retrofit-gasket", title: "EPDM Gasket Lines",
    use: "The seal: Pres-On P8100ul foam tape seated in every extrusion slot — air-tight, vapor-tight, and vibration-damped so the envelope holds pressure and humidity.",
  },
  {
    id: "retrofit-anchor", title: "Structural Floor Anchors",
    use: "The footing: powder-coated flange bases bolt each frame post to the floor — the envelope becomes a permanent structure, not a tent accessory.",
  },
  {
    id: "retrofit-hooks", title: "2020 Sliding Hooks",
    use: "The interface: the 8-pack of sliding hooks clamps the tent's existing steel poles and carries the ETFE film and 2020 frame — the new envelope hangs from the skeleton the tent already has.",
  },
  {
    id: "retrofit-process", title: "The Build Process",
    use: "The tools: GANWEI drill jig for clean access holes, Icon ball-end hex set for the corner bolts, WORKPRO blade to trim film — measure twice, tension once.",
  },
];

export default function RetrofitGallery({ onClose }) {
  return (
    <div className="absolute inset-0 z-30 bg-stone-950/97 backdrop-blur-md overflow-y-auto" data-testid="retrofit-gallery">
      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <span className="text-[10px] font-mono tracking-widest text-amber-400/90 uppercase">Upgrade Modification · AI Photoreal Renders</span>
            <h2 className="text-2xl sm:text-3xl font-bold text-stone-100" style={{ fontFamily: "Georgia, serif" }}>
              ETFE FILM RETROFIT — THE PERMANENT ENVELOPE
            </h2>
            <p className="text-sm text-stone-400 mt-1 max-w-2xl leading-relaxed">
              The procedure: cut a large section out of the 811's fabric walls, then set framed ETFE
              fluoropolymer film into each opening — hung on sliding hooks clamped to the tent's own
              steel poles. Clearer than glass, UV-transparent, and the tent keeps its original skin.
            </p>
          </div>
          <button data-testid="retrofit-close" onClick={onClose} className="shrink-0 px-3 min-h-[44px] rounded-lg bg-stone-900 border border-slate-600/60 text-stone-300 hover:text-amber-300 font-mono text-[10px] tracking-widest">
            CLOSE ✕
          </button>
        </div>

        <div className="rounded-xl overflow-hidden border border-amber-500/25" data-testid="retrofit-hero-wrap">
          <video
            src={`${BASE}/retrofit-flythrough.mp4`}
            poster={`${BASE}/retrofit-hero.png`}
            autoPlay
            loop
            muted
            playsInline
            className="w-full max-h-[60vh] object-cover"
            data-testid="retrofit-hero-video"
            onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "block"; }}
          />
          <img src={`${BASE}/retrofit-hero.png`} alt="Photoreal render of the ETFE-film retrofitted grow structure" className="w-full max-h-[60vh] object-cover" style={{ display: "none" }} />
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          {ITEMS.map((it) => (
            <figure key={it.id} className="rounded-xl overflow-hidden border border-slate-700/50 bg-stone-950/80" data-testid={`retrofit-item-${it.id}`}>
              <img src={`${BASE}/${it.id}.png`} alt={`Photoreal render: ${it.title}`} className="w-full h-52 object-cover" loading="lazy" />
              <figcaption className="p-4">
                <div className="text-sm font-bold text-stone-100" style={{ fontFamily: "Georgia, serif" }}>{it.title}</div>
                <p className="text-xs text-stone-400 leading-relaxed mt-1">{it.use}</p>
              </figcaption>
            </figure>
          ))}
        </div>
        <p className="text-[10px] font-mono tracking-widest text-stone-500 text-center pb-4">
          ALL RENDERS AI-GENERATED FROM YOUR ACTUAL PARTS LIST · PHOTOREFERENCE BEFORE ORDERING
        </p>
      </div>
    </div>
  );
}
